import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import { TeamsService } from '../../../services/teams/teams.service';
import {
  PayrollDashboardActionDefinition,
  PayrollDashboardDefinition,
  PayrollDashboardMetricType,
  PayrollDashboardPermission,
  PayrollDashboardPermissionSubjectType,
  PayrollDashboardTileDefinition,
  PayrollDashboardValueFormat,
  PayrollDashboardsService,
} from '../../../services/payroll-dashboards/payroll-dashboards.service';
import {
  buildScheduledReportValueOptions,
  ScheduledReport,
  ScheduledReportDefinitionFilter,
  ScheduledReportFieldDefinition,
  ScheduledReportPreviewOverrides,
  ScheduledReportRunDeliveryOverride,
  ScheduledReportPreview,
  ScheduledReportTemplate,
  ScheduledReportValueOption,
  ScheduledReportsService,
} from '../../../services/scheduled-reports/scheduled-reports.service';
import { UsersService } from '../../../services/users/users.service';
import {
  PayrollDashboardSendReportDialogComponent,
  PayrollDashboardSendReportDialogResult,
} from './payroll-dashboard-send-report-dialog.component';
import {
  PayrollDashboardReportParameterDialogResult,
  PayrollDashboardReportParametersDialogComponent,
} from './payroll-dashboard-report-parameters-dialog.component';
import { SessionService } from '../../../services/session/session.service';

type DashboardViewMode = 'list' | 'create' | 'edit' | 'detail';
type TileRenderStatus = 'success' | 'error';
type TilePreviewSortDirection = 'asc' | 'desc';

interface DashboardColumnOption {
  value: string;
  label: string;
}

interface DashboardPreviewColumn {
  header: string;
  field: string | null;
}

interface DashboardTileRenderResult {
  tileId: string;
  title: string;
  reportId: string;
  reportName: string;
  reportDescription: string;
  previewColumns: DashboardPreviewColumn[];
  previewRows: Array<Record<string, any>>;
  previewTotalCount: number;
  previewTimezone?: string;
  status: TileRenderStatus;
  displayValue: string;
  helperText: string;
  warnings: string[];
  errorMessage?: string;
  rawValue?: number | null;
}

interface DashboardRenderState {
  renderedAt: string;
  tiles: DashboardTileRenderResult[];
  warnings: string[];
}

interface DashboardFormTileValue {
  title?: string;
  reportId?: string;
  metricType?: string;
  fieldKey?: string;
}

interface DashboardFormActionValue {
  label?: string;
  targetType?: string;
  reportId?: string;
  templateKey?: string;
}

interface DashboardFormPermissionValue {
  subjectType?: string;
  subjectId?: string;
}

interface DashboardPermissionOption {
  id: string;
  label: string;
  secondaryLabel?: string;
}

@Component({
  selector: 'app-payroll-dashboards',
  templateUrl: './payroll-dashboards.component.html',
  styleUrls: ['./payroll-dashboards.component.scss'],
})
export class PayrollDashboardsComponent implements OnInit, OnDestroy {
  private readonly detailPreviewLimit = 5000;
  private readonly tilePreviewRowLimit = 25;

  protected isLoading = true;
  protected isBootstrapping = false;
  protected isSaving = false;
  protected isRendering = false;
  protected isLoadingPermissions = false;
  protected viewMode: DashboardViewMode = 'list';

  protected dashboards: PayrollDashboardDefinition[] = [];
  protected reports: ScheduledReport[] = [];
  protected templates: ScheduledReportTemplate[] = [];
  protected fieldCatalog: ScheduledReportFieldDefinition[] = [];
  protected reportValueOptions: Record<string, ScheduledReportValueOption[]> = {};
  protected detailDashboard: PayrollDashboardDefinition | null = null;
  protected detailRenderState: DashboardRenderState | null = null;
  protected editorRenderState: DashboardRenderState | null = null;
  protected searchValue = '';
  protected statusFilter = 'all';
  protected validationMessages: string[] = [];
  protected openPreviewTileIds: Record<string, boolean> = {};
  protected canManageDashboards = false;
  protected downloadingReportTileIds = new Set<string>();
  protected tilePreviewSortState: Record<string, { header: string; direction: TilePreviewSortDirection }> = {};

  protected readonly metricTypeOptions: Array<{ value: PayrollDashboardMetricType; label: string }> = [
    { value: 'count', label: 'Count rows' },
    { value: 'sum', label: 'Sum values' },
    { value: 'average', label: 'Average values' },
    { value: 'minimum', label: 'Minimum value' },
    { value: 'maximum', label: 'Maximum value' },
  ];

  protected readonly valueFormatOptions: Array<{ value: PayrollDashboardValueFormat; label: string }> = [
    { value: 'number', label: 'Number' },
    { value: 'currency', label: 'Currency' },
    { value: 'percent', label: 'Percent' },
  ];

  protected readonly permissionSubjectTypeOptions: Array<{ value: PayrollDashboardPermissionSubjectType; label: string }> = [
    { value: 'user', label: 'User' },
    { value: 'company', label: 'Company' },
    { value: 'team', label: 'Team' },
    { value: 'brand', label: 'Brand' },
  ];

  protected readonly dashboardForm = this.formBuilder.group({
    name: ['', Validators.required],
    description: [''],
    status: ['draft', Validators.required],
    tiles: this.formBuilder.array([]),
    actions: this.formBuilder.array([]),
    permissions: this.formBuilder.array([]),
  });

  protected editingDashboardId = '';

  private meSubscription?: Subscription;
  private workspaceLoaded = false;
  private tileColumnOptionsByReportId: Record<string, DashboardColumnOption[]> = {};
  private tileColumnLoadingIds = new Set<string>();
  private readonly selectSearchThreshold = 8;
  protected readonly permissionUserSearchControl = new UntypedFormControl('');
  protected readonly permissionCompanySearchControl = new UntypedFormControl('');
  protected readonly permissionTeamSearchControl = new UntypedFormControl('');
  protected readonly permissionBrandSearchControl = new UntypedFormControl('');
  protected permissionUserOptions: DashboardPermissionOption[] = [];
  protected permissionCompanyOptions: DashboardPermissionOption[] = [];
  protected permissionTeamOptions: DashboardPermissionOption[] = [];
  protected permissionBrandOptions: DashboardPermissionOption[] = [];
  protected permissionOptionLoadingTypes = new Set<string>();
  private permissionLoadedTypes = new Set<string>();
  private permissionLoadPromises = new Map<PayrollDashboardPermissionSubjectType, Promise<void>>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private usersService: UsersService,
    private teamsService: TeamsService,
    private teamCompaniesService: TeamCompaniesService,
    private dashboardsService: PayrollDashboardsService,
    private scheduledReportsService: ScheduledReportsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    this.canManageDashboards = this.sessionService.isSuperAdmin();
    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (!me || this.workspaceLoaded) {
        return;
      }

      this.workspaceLoaded = true;
      await this.loadWorkspace();
    });
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
  }

  protected get filteredDashboards(): PayrollDashboardDefinition[] {
    const search = this.searchValue.trim().toLowerCase();
    return this.dashboards.filter((dashboard) => {
      if (this.statusFilter !== 'all' && dashboard.status !== this.statusFilter) {
        return false;
      }
      if (!search) {
        return true;
      }

      return [
        dashboard.name,
        dashboard.description,
        dashboard.status,
        dashboard.tiles.map((tile) => tile.title).join(' '),
        dashboard.actions.map((action) => action.label).join(' '),
      ].some((value) => `${value || ''}`.toLowerCase().includes(search));
    });
  }

  protected get tilesArray(): UntypedFormArray {
    return this.dashboardForm.get('tiles') as UntypedFormArray;
  }

  protected get actionsArray(): UntypedFormArray {
    return this.dashboardForm.get('actions') as UntypedFormArray;
  }

  protected get permissionsArray(): UntypedFormArray {
    return this.dashboardForm.get('permissions') as UntypedFormArray;
  }

  protected async refreshList(): Promise<void> {
    await this.loadDashboards();
  }

  protected startCreate(): void {
    if (!this.canManageDashboards) {
      return;
    }

    this.viewMode = 'create';
    this.detailDashboard = null;
    this.detailRenderState = null;
    this.editorRenderState = null;
    this.openPreviewTileIds = {};
    this.editingDashboardId = '';
    this.validationMessages = [];
    this.dashboardForm.reset({
      name: '',
      description: '',
      status: 'draft',
    });
    this.replaceFormArray(this.tilesArray, []);
    this.replaceFormArray(this.actionsArray, []);
    this.replaceFormArray(this.permissionsArray, []);
    this.addTile();
  }

  protected async openDashboard(dashboard: PayrollDashboardDefinition): Promise<void> {
    this.viewMode = 'detail';
    this.detailDashboard = dashboard;
    this.detailRenderState = null;
    this.openPreviewTileIds = {};
    await this.renderDashboard(dashboard, 'detail');
  }

  protected async editDashboard(dashboard: PayrollDashboardDefinition): Promise<void> {
    if (!this.canManageDashboards) {
      return;
    }

    this.viewMode = 'edit';
    this.detailDashboard = dashboard;
    this.detailRenderState = null;
    this.editorRenderState = null;
    this.openPreviewTileIds = {};
    this.validationMessages = [];
    this.editingDashboardId = dashboard.id;
    this.dashboardForm.reset({
      name: dashboard.name,
      description: dashboard.description || '',
      status: dashboard.status,
    });
    this.replaceFormArray(this.tilesArray, dashboard.tiles.map((tile) => this.buildTileGroup(tile)));
    this.replaceFormArray(this.actionsArray, dashboard.actions.map((action) => this.buildActionGroup(action)));
    this.replaceFormArray(this.permissionsArray, []);
    await this.loadPermissionsForDashboard(dashboard.id);
    void this.primeTileFieldOptions();
  }

  protected returnToList(): void {
    this.viewMode = 'list';
    this.detailDashboard = null;
    this.detailRenderState = null;
    this.editorRenderState = null;
    this.openPreviewTileIds = {};
    this.validationMessages = [];
  }

  protected addTile(): void {
    const control = this.buildTileGroup();
    this.tilesArray.push(control);
    void this.onTileReportChange(control);
  }

  protected removeTile(index: number): void {
    this.tilesArray.removeAt(index);
  }

  protected addAction(): void {
    this.actionsArray.push(this.buildActionGroup());
  }

  protected removeAction(index: number): void {
    this.actionsArray.removeAt(index);
  }

  protected addPermission(permission?: Partial<PayrollDashboardPermission>): void {
    this.permissionsArray.push(this.buildPermissionGroup(permission));
    void this.ensurePermissionOptionsLoaded(`${permission?.subjectType || 'company'}`);
  }

  protected removePermission(index: number): void {
    this.permissionsArray.removeAt(index);
  }

  protected onPermissionSubjectTypeChange(control: AbstractControl): void {
    control.get('subjectId')?.setValue('');
    void this.ensurePermissionOptionsLoaded(`${control.get('subjectType')?.value || ''}`);
  }

  protected onPermissionSubjectSelectOpened(control: AbstractControl, opened: boolean): void {
    if (opened) {
      void this.ensurePermissionOptionsLoaded(`${control.get('subjectType')?.value || ''}`);
    }
  }

  protected async previewDashboard(): Promise<void> {
    this.validationMessages = this.collectValidationMessages();
    if (this.validationMessages.length > 0) {
      this.handleValidationFailure();
      return;
    }

    await this.renderDashboard(this.buildDashboardFromForm(), 'editor');
  }

  protected async saveDashboard(): Promise<void> {
    if (!this.canManageDashboards) {
      this.snackBar.open('You do not have permission to save dashboards.', 'Dismiss', { duration: 4000 });
      return;
    }

    this.validationMessages = this.collectValidationMessages();
    if (this.validationMessages.length > 0) {
      this.handleValidationFailure();
      return;
    }

    this.isSaving = true;
    try {
      const dashboard = this.buildDashboardFromForm();
      const savedDashboard = await this.dashboardsService.upsertDashboard(dashboard, !!this.editingDashboardId) || dashboard;
      await this.dashboardsService.replacePermissions(savedDashboard.id, this.buildPermissionsFromForm(savedDashboard.id));
      await this.loadDashboards();
      const saved = this.dashboards.find((item) => item.id === savedDashboard.id) || {
        ...savedDashboard,
        permissionCount: this.permissionsArray.length,
      };
      this.snackBar.open('Dashboard saved.', 'Dismiss', { duration: 2500 });
      await this.openDashboard(saved);
    } catch (error: any) {
      this.snackBar.open(error?.message || 'Unable to save dashboard.', 'Dismiss', { duration: 4000 });
    } finally {
      this.isSaving = false;
    }
  }

  protected async confirmDeleteDashboard(dashboard: PayrollDashboardDefinition, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.canManageDashboards) {
      this.snackBar.open('You do not have permission to delete dashboards.', 'Dismiss', { duration: 4000 });
      return;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Delete Dashboard',
        `Delete dashboard <strong>${dashboard.name}</strong>?`,
        false,
        'Delete',
        'Cancel',
      ),
      autoFocus: false,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) {
      return;
    }

    try {
      await this.dashboardsService.deleteDashboard(dashboard.id);
      await this.loadDashboards();
      if (this.detailDashboard?.id === dashboard.id) {
        this.returnToList();
      }
      this.snackBar.open('Dashboard deleted.', 'Dismiss', { duration: 2500 });
    } catch (error: any) {
      this.snackBar.open(error?.message || 'Unable to delete dashboard.', 'Dismiss', { duration: 4000 });
    }
  }

  protected async refreshDetail(): Promise<void> {
    if (!this.detailDashboard) {
      return;
    }
    await this.renderDashboard(this.detailDashboard, 'detail');
  }

  protected async onTileReportChange(control: AbstractControl): Promise<void> {
    const reportId = `${control.get('reportId')?.value || ''}`.trim();
    if (!reportId) {
      return;
    }

    if (`${control.get('metricType')?.value || 'count'}` === 'count') {
      return;
    }

    await this.ensureTileFieldOptions(reportId);
    const currentField = `${control.get('fieldKey')?.value || ''}`.trim();
    const options = this.tileColumnOptionsByReportId[reportId] || [];
    if (!currentField && options.length) {
      control.get('fieldKey')?.setValue(options[0].value);
    }
  }

  protected onTileMetricTypeChange(control: AbstractControl): void {
    if (`${control.get('metricType')?.value || 'count'}` === 'count') {
      control.get('fieldKey')?.setValue('');
      return;
    }

    const reportId = `${control.get('reportId')?.value || ''}`.trim();
    const options = this.tileColumnOptionsByReportId[reportId] || [];
    const currentField = `${control.get('fieldKey')?.value || ''}`.trim();
    if (!currentField && options.length) {
      control.get('fieldKey')?.setValue(options[0].value);
    }
  }

  protected getTileFieldOptions(control: AbstractControl): DashboardColumnOption[] {
    const reportId = `${control.get('reportId')?.value || ''}`.trim();
    return this.tileColumnOptionsByReportId[reportId] || [];
  }

  protected isTileFieldOptionsLoading(control: AbstractControl): boolean {
    const reportId = `${control.get('reportId')?.value || ''}`.trim();
    return this.tileColumnLoadingIds.has(reportId);
  }

  protected getTileFieldHint(control: AbstractControl): string {
    const options = this.getTileFieldOptions(control);
    if (!options.length) {
      return 'Load preview columns from the selected report.';
    }

    return `Available columns: ${options.slice(0, 4).map((item) => item.label).join(', ')}${options.length > 4 ? '...' : ''}`;
  }

  protected getReportName(reportId: string | null | undefined): string {
    if (!reportId) {
      return '--';
    }
    return this.reports.find((report) => report.id === reportId)?.name || reportId;
  }

  protected getTemplateLabel(templateKey: string | null | undefined): string {
    if (!templateKey) {
      return '--';
    }
    return this.templates.find((template) => template.key === templateKey)?.label || templateKey;
  }

  protected getMetricTypeLabel(metricType: PayrollDashboardMetricType | string | null | undefined): string {
    return this.metricTypeOptions.find((item) => item.value === metricType)?.label || `${metricType || 'Count rows'}`;
  }

  protected getValueFormatLabel(format: PayrollDashboardValueFormat | string | null | undefined): string {
    return this.valueFormatOptions.find((item) => item.value === format)?.label || `${format || 'Number'}`;
  }

  protected getPermissionSubjectTypeLabel(subjectType: PayrollDashboardPermissionSubjectType | string | null | undefined): string {
    return this.permissionSubjectTypeOptions.find((item) => item.value === subjectType)?.label || `${subjectType || 'Access'}`;
  }

  protected getPermissionSubjectLoadingLabel(subjectType: PayrollDashboardPermissionSubjectType | string | null | undefined): string {
    switch (subjectType) {
      case 'user':
        return 'users';
      case 'company':
        return 'companies';
      case 'team':
        return 'teams';
      case 'brand':
        return 'brands';
      default:
        return 'access entries';
    }
  }

  protected getPermissionSubjectSelectionLabel(control: AbstractControl): string {
    const subjectType = `${control.get('subjectType')?.value || ''}`;
    const subjectId = `${control.get('subjectId')?.value || ''}`;
    const subjectLabel = this.getPermissionSubjectTypeLabel(subjectType);
    if (!subjectId) {
      return `${subjectLabel} required`;
    }

    const option = this.getPermissionSubjectOptions(subjectType).find((item) => item.id === subjectId);
    if (option) {
      return `${option.label}${option.secondaryLabel ? ` - ${option.secondaryLabel}` : ''}`;
    }

    return `${subjectLabel} selected`;
  }

  protected shouldShowSelectSearch(optionCount: number): boolean {
    return optionCount > this.selectSearchThreshold;
  }

  protected getPermissionSearchControl(subjectType: string | null | undefined): UntypedFormControl {
    switch (subjectType) {
      case 'user':
        return this.permissionUserSearchControl;
      case 'team':
        return this.permissionTeamSearchControl;
      case 'brand':
        return this.permissionBrandSearchControl;
      case 'company':
      default:
        return this.permissionCompanySearchControl;
    }
  }

  protected getPermissionSubjectOptions(subjectType: string | null | undefined): DashboardPermissionOption[] {
    switch (subjectType) {
      case 'user':
        return this.permissionUserOptions;
      case 'team':
        return this.permissionTeamOptions;
      case 'brand':
        return this.permissionBrandOptions;
      case 'company':
      default:
        return this.permissionCompanyOptions;
    }
  }

  protected getFilteredPermissionSubjectOptions(subjectType: string | null | undefined): DashboardPermissionOption[] {
    const search = `${this.getPermissionSearchControl(subjectType).value || ''}`.trim();
    return this.filterItems(this.getPermissionSubjectOptions(subjectType), search, (option) => [
      option.label,
      option.secondaryLabel,
    ]);
  }

  protected isPermissionOptionsLoading(subjectType: string | null | undefined): boolean {
    return this.permissionOptionLoadingTypes.has(`${subjectType || ''}`);
  }

  protected async openAction(action: PayrollDashboardActionDefinition): Promise<void> {
    if (action.targetType === 'saved_report' && action.reportId) {
      await this.router.navigate(['/payrolladmin/reports'], {
        queryParams: {
          reportId: action.reportId,
        },
      });
      return;
    }

    if (action.targetType === 'report_template' && action.templateKey) {
      await this.router.navigate(['/payrolladmin/reports'], {
        queryParams: {
          create: 'template',
          templateKey: action.templateKey,
        },
      });
    }
  }

  protected async openTileReport(tile: DashboardTileRenderResult): Promise<void> {
    if (!tile.reportId) {
      return;
    }

    await this.router.navigate(['/payrolladmin/reports'], {
      queryParams: {
        reportId: tile.reportId,
      },
    });
  }

  protected async sendTileReport(tile: DashboardTileRenderResult): Promise<void> {
    if (!tile.reportId) {
      return;
    }

    const report = this.reports.find((item) => item.id === tile.reportId) || null;
    const dialogRef = this.dialog.open<
      PayrollDashboardSendReportDialogComponent,
      { reportName: string },
      PayrollDashboardSendReportDialogResult
    >(PayrollDashboardSendReportDialogComponent, {
      width: '520px',
      data: {
        reportName: report?.name || tile.reportName || tile.title || 'report',
      },
      autoFocus: false,
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result?.email) {
      return;
    }

    try {
      const runtimeParameters = await this.promptRuntimeParameters(report);
      if (runtimeParameters === null) {
        return;
      }

      const runResult = await this.scheduledReportsService.runNowWithDeliveryOverride(
        tile.reportId,
        this.buildDashboardEmailDeliveryOverride(report, result.email),
        runtimeParameters || undefined,
      );
      const rowText = runResult.rowsGenerated === 1 ? '1 row' : `${runResult.rowsGenerated || 0} rows`;
      this.snackBar.open(`Report sent to ${result.email} with ${rowText}.`, 'Dismiss', { duration: 4000 });
    } catch (error: any) {
      this.snackBar.open(error?.message || 'Unable to send the report.', 'Dismiss', { duration: 4000 });
    }
  }

  protected async downloadTileReport(tile: DashboardTileRenderResult): Promise<void> {
    if (!this.detailDashboard?.id || !tile.reportId || this.isTileReportDownloading(tile)) {
      return;
    }

    const tileId = tile.tileId || tile.reportId;
    this.downloadingReportTileIds = new Set([...this.downloadingReportTileIds, tileId]);
    try {
      const report = this.reports.find((item) => item.id === tile.reportId) || null;
      const runtimeParameters = await this.promptRuntimeParameters(report);
      if (runtimeParameters === null) {
        return;
      }
      const overrides: ScheduledReportPreviewOverrides | undefined = runtimeParameters
        ? { runtimeParameters }
        : undefined;
      const runResult = await this.dashboardsService.downloadDashboardReport(this.detailDashboard.id, tile.reportId, overrides);
      if (!runResult.artifactUrl) {
        this.snackBar.open('Report did not produce a downloadable artifact.', 'Dismiss', { duration: 4000 });
        return;
      }

      window.open(runResult.artifactUrl, '_blank', 'noopener');
      this.snackBar.open('Report download started.', 'Dismiss', { duration: 3000 });
    } catch (error: any) {
      this.snackBar.open(error?.message || 'Unable to download the report.', 'Dismiss', { duration: 4000 });
    } finally {
      const nextDownloadingIds = new Set(this.downloadingReportTileIds);
      nextDownloadingIds.delete(tileId);
      this.downloadingReportTileIds = nextDownloadingIds;
    }
  }

  protected isTileReportDownloading(tile: DashboardTileRenderResult): boolean {
    return this.downloadingReportTileIds.has(tile.tileId || tile.reportId);
  }

  protected toggleTilePreview(tile: DashboardTileRenderResult): void {
    if (!tile?.tileId) {
      return;
    }

    this.openPreviewTileIds = {
      ...this.openPreviewTileIds,
      [tile.tileId]: !this.openPreviewTileIds[tile.tileId],
    };
  }

  protected isTilePreviewOpen(tile: DashboardTileRenderResult): boolean {
    return !!this.openPreviewTileIds[tile.tileId];
  }

  protected getTilePreviewHeaders(tile: DashboardTileRenderResult): string[] {
    return (tile.previewColumns || []).map((column, index) => column.header || column.field || `Column ${index + 1}`);
  }

  protected getTilePreviewCellValue(tile: DashboardTileRenderResult, row: Record<string, any>, header: string): any {
    if (!row) {
      return undefined;
    }

    if (row[header] !== undefined) {
      return row[header];
    }

    const column = (tile.previewColumns || []).find((item) => (item.header || item.field) === header);
    if (column?.field && row[column.field] !== undefined) {
      return row[column.field];
    }

    return undefined;
  }

  protected getTilePreviewCellText(tile: DashboardTileRenderResult, value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    const formattedDate = this.formatTilePreviewDateValue(tile, value);
    if (formattedDate !== null) {
      return formattedDate;
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return `${value}`;
  }

  protected toggleTilePreviewSort(tile: DashboardTileRenderResult, header: string): void {
    const tileId = this.getTilePreviewSortKey(tile);
    const current = this.tilePreviewSortState[tileId];
    if (!current || current.header !== header) {
      this.tilePreviewSortState = {
        ...this.tilePreviewSortState,
        [tileId]: { header, direction: 'asc' },
      };
      return;
    }

    this.tilePreviewSortState = {
      ...this.tilePreviewSortState,
      [tileId]: {
        header,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
      },
    };
  }

  protected getTilePreviewSortDirection(tile: DashboardTileRenderResult, header: string): TilePreviewSortDirection | null {
    const current = this.tilePreviewSortState[this.getTilePreviewSortKey(tile)];
    return current?.header === header ? current.direction : null;
  }

  protected getSortedTilePreviewRows(tile: DashboardTileRenderResult): Array<Record<string, any>> {
    const rows = tile.previewRows || [];
    const current = this.tilePreviewSortState[this.getTilePreviewSortKey(tile)];
    if (!current?.header) {
      return rows;
    }

    return [...rows].sort((left, right) => {
      const leftValue = this.getTilePreviewSortValue(tile, this.getTilePreviewCellValue(tile, left, current.header));
      const rightValue = this.getTilePreviewSortValue(tile, this.getTilePreviewCellValue(tile, right, current.header));
      if (leftValue < rightValue) {
        return current.direction === 'asc' ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return current.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  protected getTilePreviewSummary(tile: DashboardTileRenderResult): string {
    const rowCount = tile.previewRows.length;
    const totalCount = Number(tile.previewTotalCount || 0);
    const totalText = totalCount > 0 ? ` of ${totalCount}` : '';
    return `Preview only. Showing ${rowCount}${totalText} rows from this report, not the full delivered report output.`;
  }

  protected asFormGroup(control: AbstractControl): UntypedFormGroup {
    return control as UntypedFormGroup;
  }

  protected trackByDashboardId(index: number, dashboard: PayrollDashboardDefinition): string {
    return dashboard?.id || `${index}`;
  }

  protected trackByReportId(index: number, report: ScheduledReport): string {
    return report?.id || `${index}`;
  }

  protected trackByControlId(index: number, control: AbstractControl): string {
    return `${control.get('id')?.value || index}`;
  }

  protected trackByRenderedTile(index: number, tile: DashboardTileRenderResult): string {
    return tile.tileId || `${index}`;
  }

  protected trackByPermissionControl(index: number, control: AbstractControl): string {
    return `${control.get('rowId')?.value || control.get('id')?.value || index}`;
  }

  protected trackByPermissionOption(index: number, option: DashboardPermissionOption): string {
    return option?.id || `${index}`;
  }

  private async loadWorkspace(): Promise<void> {
    this.isBootstrapping = true;
    try {
      if (this.canManageDashboards) {
        await Promise.all([
          this.loadDashboards(),
          this.loadReportsAndTemplates(),
        ]);
      } else {
        await this.loadDashboards();
      }
    } finally {
      this.isLoading = false;
      this.isBootstrapping = false;
    }
  }

  private async loadDashboards(): Promise<void> {
    this.dashboards = this.canManageDashboards
      ? await this.dashboardsService.listDashboards()
      : await this.dashboardsService.listAllowedDashboards();
  }

  private async loadReportsAndTemplates(): Promise<void> {
    if (!this.canManageDashboards) {
      return;
    }

    try {
      const [reports, templateResponse, fieldCatalogResponse] = await Promise.all([
        this.scheduledReportsService.listReports({ limit: 200, offset: 0 }),
        this.scheduledReportsService.getTemplates(),
        this.scheduledReportsService.getFieldCatalog(),
      ]);
      this.reports = reports || [];
      this.templates = templateResponse.templates || [];
      this.fieldCatalog = fieldCatalogResponse.fields || [];
      this.reportValueOptions = buildScheduledReportValueOptions(fieldCatalogResponse);
    } catch (error: any) {
      this.snackBar.open(error?.message || 'Unable to load scheduled reports for dashboards.', 'Dismiss', { duration: 4000 });
      this.reports = [];
      this.templates = [];
    }
  }

  private buildTileGroup(tile?: Partial<PayrollDashboardTileDefinition>): UntypedFormGroup {
    return this.formBuilder.group({
      id: [tile?.id || this.dashboardsService.createTileId()],
      title: [tile?.title || '', Validators.required],
      description: [tile?.description || ''],
      reportId: [tile?.reportId || this.reports[0]?.id || '', Validators.required],
      metricType: [tile?.metricType || 'count', Validators.required],
      fieldKey: [tile?.fieldKey || ''],
      valueFormat: [tile?.valueFormat || 'number', Validators.required],
      precision: [tile?.precision ?? 0, Validators.required],
    });
  }

  private buildActionGroup(action?: Partial<PayrollDashboardActionDefinition>): UntypedFormGroup {
    return this.formBuilder.group({
      id: [action?.id || this.dashboardsService.createActionId()],
      label: [action?.label || '', Validators.required],
      description: [action?.description || ''],
      targetType: [action?.targetType || 'saved_report', Validators.required],
      reportId: [action?.reportId || this.reports[0]?.id || ''],
      templateKey: [action?.templateKey || this.templates[0]?.key || ''],
    });
  }

  private buildPermissionGroup(permission?: Partial<PayrollDashboardPermission>): UntypedFormGroup {
    return this.formBuilder.group({
      rowId: [this.createPermissionRowId()],
      id: [permission?.id || ''],
      subjectType: [permission?.subjectType || 'company', Validators.required],
      subjectId: [permission?.subjectId || '', Validators.required],
    });
  }

  private replaceFormArray(target: UntypedFormArray, values: UntypedFormGroup[]): void {
    while (target.length) {
      target.removeAt(0);
    }
    values.forEach((value) => target.push(value));
  }

  private handleValidationFailure(): void {
    this.dashboardForm.markAllAsTouched();
    this.snackBar.open(this.validationMessages[0] || 'Fix the validation errors before continuing.', 'Dismiss', { duration: 4000 });
  }

  private async primeTileFieldOptions(): Promise<void> {
    const promises = this.tilesArray.controls.map((control) => this.onTileReportChange(control));
    await Promise.all(promises);
  }

  private async loadPermissionsForDashboard(dashboardId: string): Promise<void> {
    if (!dashboardId) {
      return;
    }

    this.isLoadingPermissions = true;
    try {
      const response = await this.dashboardsService.getPermissions(dashboardId);
      this.replaceFormArray(
        this.permissionsArray,
        response.permissions.map((permission) => this.buildPermissionGroup(permission)),
      );
      await Promise.all(Array.from(new Set(response.permissions.map((permission) => permission.subjectType)))
        .map((subjectType) => this.ensurePermissionOptionsLoaded(subjectType)));
    } catch (error: any) {
      this.replaceFormArray(this.permissionsArray, []);
      this.snackBar.open(error?.message || 'Unable to load dashboard permissions.', 'Dismiss', { duration: 4000 });
    } finally {
      this.isLoadingPermissions = false;
    }
  }

  private ensurePermissionOptionsLoaded(subjectType: string): Promise<void> {
    if (!this.isPermissionSubjectType(subjectType) || this.permissionLoadedTypes.has(subjectType)) {
      return Promise.resolve();
    }

    const existingPromise = this.permissionLoadPromises.get(subjectType);
    if (existingPromise) {
      return existingPromise;
    }

    this.permissionOptionLoadingTypes.add(subjectType);
    const loadPromise = this.loadPermissionOptions(subjectType).finally(() => {
      this.permissionOptionLoadingTypes.delete(subjectType);
      this.permissionLoadPromises.delete(subjectType);
    });
    this.permissionLoadPromises.set(subjectType, loadPromise);

    return loadPromise;
  }

  private async loadPermissionOptions(subjectType: PayrollDashboardPermissionSubjectType): Promise<void> {
    if (this.permissionLoadedTypes.has(subjectType)) {
      return;
    }

    if (subjectType === 'user') {
      this.permissionUserOptions = await this.loadUserPermissionOptions();
    } else if (subjectType === 'company') {
      this.permissionCompanyOptions = await this.loadCompanyPermissionOptions();
    } else if (subjectType === 'team') {
      this.permissionTeamOptions = await this.loadTeamPermissionOptions();
    } else if (subjectType === 'brand') {
      this.permissionBrandOptions = await this.loadBrandPermissionOptions();
    }

    this.permissionLoadedTypes.add(subjectType);
  }

  private async loadUserPermissionOptions(): Promise<DashboardPermissionOption[]> {
    try {
      const users = this.normalizeResponseList(await this.usersService.searchAccounts('', false, false, '', 0, 10000));
      return this.normalizeUsers(users);
    } catch {
      return [];
    }
  }

  private async loadCompanyPermissionOptions(): Promise<DashboardPermissionOption[]> {
    try {
      return this.normalizeCompanies(this.normalizeResponseList(await this.teamCompaniesService.getCompanies()));
    } catch {
      return [];
    }
  }

  private async loadTeamPermissionOptions(): Promise<DashboardPermissionOption[]> {
    try {
      return this.normalizeTeams(await this.teamsService.getAllTeams());
    } catch {
      return [];
    }
  }

  private async loadBrandPermissionOptions(): Promise<DashboardPermissionOption[]> {
    try {
      return this.normalizeBrands(await this.usersService.getVisibleBrands());
    } catch {
      return [];
    }
  }

  private normalizeUsers(users: any[]): DashboardPermissionOption[] {
    return this.sortAndDeduplicatePermissionOptions((users || [])
      .map((user: any) => {
        const label = this.buildUserLabel(user);
        const email = `${user?.email || ''}`.trim();
        return {
          id: user.id || user.userId,
          label,
          secondaryLabel: email && email !== label ? email : '',
        };
      })
      .filter((user: DashboardPermissionOption) => !!user.id));
  }

  private normalizeCompanies(companies: any[], brandLabel = ''): DashboardPermissionOption[] {
    return this.sortAndDeduplicatePermissionOptions((companies || []).map((company: any) => ({
      id: company.id || company.companyId,
      label: company.name || company.companyName || 'Untitled company',
      secondaryLabel: brandLabel,
    })).filter((company: DashboardPermissionOption) => !!company.id));
  }

  private normalizeBrands(brands: any[]): DashboardPermissionOption[] {
    return this.sortAndDeduplicatePermissionOptions((brands || [])
      .map((brand: any) => ({
        id: brand.partnerId || brand.id || brand.shortName,
        label: brand.name || brand.shortName || brand.partnerId || 'Untitled brand',
        secondaryLabel: brand.shortName || '',
      }))
      .filter((brand: DashboardPermissionOption) => !!brand.id));
  }

  private normalizeTeams(teams: any[], companyLabel = ''): DashboardPermissionOption[] {
    return this.sortAndDeduplicatePermissionOptions((teams || [])
      .map((team: any) => ({
        id: team.id,
        label: team.name || 'Untitled team',
        secondaryLabel: companyLabel || team.companyName || team.company?.name || '',
      }))
      .filter((team: DashboardPermissionOption) => !!team.id));
  }

  private sortAndDeduplicatePermissionOptions(options: DashboardPermissionOption[]): DashboardPermissionOption[] {
    const collected = new Map<string, DashboardPermissionOption>();
    options.forEach((option) => {
      if (!option?.id || collected.has(option.id)) {
        return;
      }
      collected.set(option.id, option);
    });
    return Array.from(collected.values()).sort((left, right) => left.label.localeCompare(right.label));
  }

  private buildUserLabel(user: any): string {
    const name = [user?.firstName, user?.lastName].map((value) => {
      const namePart = `${value || ''}`.trim();
      return namePart === '-' ? '' : namePart;
    }).filter(Boolean).join(' ');
    return name || user?.email || user?.username || 'Untitled user';
  }

  private normalizeResponseList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response?.items)) {
      return response.items;
    }
    if (Array.isArray(response?.results)) {
      return response.results;
    }
    if (Array.isArray(response?.rows)) {
      return response.rows;
    }
    if (typeof response?.plain === 'function') {
      const plain = response.plain();
      return Array.isArray(plain) ? plain : [];
    }
    return [];
  }

  private filterItems<T>(items: T[], search: string, getSearchValues: (item: T) => Array<string | null | undefined>): T[] {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return items;
    }

    return items.filter((item) => getSearchValues(item).some((value) => `${value || ''}`.toLowerCase().includes(normalizedSearch)));
  }

  private createPermissionRowId(): string {
    return `permission_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private collectValidationMessages(): string[] {
    const messages: string[] = [];
    const raw = this.dashboardForm.getRawValue();
    const name = `${raw.name || ''}`.trim();
    if (!name) {
      messages.push('Dashboard name is required.');
    }

    const tiles = Array.isArray(raw.tiles) ? raw.tiles : [];
    const actions = Array.isArray(raw.actions) ? raw.actions : [];
    const permissions = Array.isArray(raw.permissions) ? raw.permissions : [];
    if (!tiles.length && !actions.length) {
      messages.push('Add at least one tile or one report action button.');
    }

    (tiles as DashboardFormTileValue[]).forEach((tile: DashboardFormTileValue, index: number) => {
      if (!`${tile.title || ''}`.trim()) {
        messages.push(`Tile ${index + 1}: title is required.`);
      }
      if (!`${tile.reportId || ''}`.trim()) {
        messages.push(`Tile ${index + 1}: report is required.`);
      }
      if (`${tile.metricType || 'count'}` !== 'count' && !`${tile.fieldKey || ''}`.trim()) {
        messages.push(`Tile ${index + 1}: field is required for ${tile.metricType}.`);
      }
    });

    (actions as DashboardFormActionValue[]).forEach((action: DashboardFormActionValue, index: number) => {
      if (!`${action.label || ''}`.trim()) {
        messages.push(`Action ${index + 1}: label is required.`);
      }
      if (`${action.targetType || 'saved_report'}` === 'saved_report' && !`${action.reportId || ''}`.trim()) {
        messages.push(`Action ${index + 1}: choose a saved report.`);
      }
      if (`${action.targetType || 'saved_report'}` === 'report_template' && !`${action.templateKey || ''}`.trim()) {
        messages.push(`Action ${index + 1}: choose a report template.`);
      }
    });

    const permissionKeys = new Set<string>();
    (permissions as DashboardFormPermissionValue[]).forEach((permission: DashboardFormPermissionValue, index: number) => {
      const subjectType = `${permission.subjectType || ''}`.trim();
      const subjectId = `${permission.subjectId || ''}`.trim();
      if (!this.isPermissionSubjectType(subjectType)) {
        messages.push(`Permission ${index + 1}: choose a valid access type.`);
      }
      if (!subjectId) {
        messages.push(`Permission ${index + 1}: subject ID is required.`);
      }

      const permissionKey = `${subjectType}:${subjectId}`;
      if (subjectType && subjectId) {
        if (permissionKeys.has(permissionKey)) {
          const duplicateLabel = this.getPermissionSubjectSelectionLabel(this.permissionsArray.at(index));
          messages.push(`Permission ${index + 1}: duplicate ${this.getPermissionSubjectTypeLabel(subjectType)} access for ${duplicateLabel}.`);
        }
        permissionKeys.add(permissionKey);
      }
    });

    return messages;
  }

  private buildDashboardFromForm(): PayrollDashboardDefinition {
    const raw = this.dashboardForm.getRawValue();
    const existing = this.dashboards.find((dashboard) => dashboard.id === this.editingDashboardId)
      || this.detailDashboard;
    const now = new Date().toISOString();

    return {
      id: this.editingDashboardId || this.dashboardsService.createDashboardId(),
      name: `${raw.name || ''}`.trim(),
      description: `${raw.description || ''}`.trim(),
      status: `${raw.status || 'draft'}` as PayrollDashboardDefinition['status'],
      tiles: (Array.isArray(raw.tiles) ? raw.tiles : []).map((tile: any) => ({
        id: `${tile.id || this.dashboardsService.createTileId()}`,
        title: `${tile.title || ''}`.trim(),
        description: `${tile.description || ''}`.trim(),
        reportId: `${tile.reportId || ''}`.trim(),
        metricType: `${tile.metricType || 'count'}` as PayrollDashboardMetricType,
        fieldKey: `${tile.fieldKey || ''}`.trim(),
        valueFormat: `${tile.valueFormat || 'number'}` as PayrollDashboardValueFormat,
        precision: Number.isFinite(Number(tile.precision)) ? Number(tile.precision) : 0,
      })),
      actions: (Array.isArray(raw.actions) ? raw.actions : []).map((action: any) => ({
        id: `${action.id || this.dashboardsService.createActionId()}`,
        label: `${action.label || ''}`.trim(),
        description: `${action.description || ''}`.trim(),
        targetType: `${action.targetType || 'saved_report'}` as PayrollDashboardActionDefinition['targetType'],
        reportId: `${action.reportId || ''}`.trim(),
        templateKey: `${action.templateKey || ''}`.trim(),
      })),
      metadata: existing?.metadata || {},
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };
  }

  private buildPermissionsFromForm(dashboardId: string): PayrollDashboardPermission[] {
    const raw = this.dashboardForm.getRawValue();
    const permissions = Array.isArray(raw.permissions) ? raw.permissions : [];
    return (permissions as DashboardFormPermissionValue[])
      .map((permission: DashboardFormPermissionValue): PayrollDashboardPermission | null => {
        const subjectType = `${permission.subjectType || ''}`.trim();
        const subjectId = `${permission.subjectId || ''}`.trim();
        if (!this.isPermissionSubjectType(subjectType) || !subjectId) {
          return null;
        }

        return {
          dashboardId,
          subjectType,
          subjectId,
        };
      })
      .filter((permission: PayrollDashboardPermission | null): permission is PayrollDashboardPermission => Boolean(permission));
  }

  private isPermissionSubjectType(value: string): value is PayrollDashboardPermissionSubjectType {
    return this.permissionSubjectTypeOptions.some((option) => option.value === value);
  }

  private async renderDashboard(definition: PayrollDashboardDefinition, target: 'detail' | 'editor'): Promise<void> {
    this.isRendering = true;
    const previewCache = new Map<string, Promise<ScheduledReportPreview>>();
    try {
      const renderedTiles = await Promise.all(definition.tiles.map((tile) => this.renderTile(tile, previewCache, definition.id)));
      const warningSet = new Set<string>();
      renderedTiles.forEach((tile: DashboardTileRenderResult) => {
        tile.warnings.forEach((warning: string) => warningSet.add(warning));
      });
      const warnings = Array.from(warningSet);
      const renderState: DashboardRenderState = {
        renderedAt: new Date().toISOString(),
        tiles: renderedTiles,
        warnings,
      };

      if (target === 'detail') {
        this.detailRenderState = renderState;
      } else {
        this.editorRenderState = renderState;
      }
    } finally {
      this.isRendering = false;
    }
  }

  private async renderTile(
    tile: PayrollDashboardTileDefinition,
    previewCache: Map<string, Promise<ScheduledReportPreview>>,
    dashboardId: string,
  ): Promise<DashboardTileRenderResult> {
    const reportId = `${tile.reportId || ''}`.trim();
    if (!reportId) {
      return this.buildTileError(tile, 'Select a valid scheduled report before rendering this tile.');
    }

    const report = this.reports.find((item) => item.id === tile.reportId) || null;
    if (!report && this.canManageDashboards) {
      return this.buildTileError(tile, 'Select a valid scheduled report before rendering this tile.');
    }

    try {
      const preview = await this.getPreview(reportId, previewCache, dashboardId);
      if (tile.metricType === 'count') {
        const countValue = Number(preview.totalCount || 0);
        return {
          tileId: tile.id,
          title: tile.title,
          reportId,
          reportName: this.getRenderedReportName(report, tile),
          reportDescription: this.getReportDescription(report),
          ...this.buildTilePreviewDisplay(preview),
          status: 'success',
          displayValue: this.formatValue(countValue, tile.valueFormat, tile.precision),
          helperText: '',
          warnings: preview.warnings || [],
          rawValue: countValue,
        };
      }

      if (!tile.fieldKey) {
        return this.buildTileError(tile, 'Choose a preview column for this aggregation.', report);
      }

      const rows = Array.isArray(preview.sampleRows) ? preview.sampleRows : [];
      const totalCount = Number(preview.totalCount || 0);
      if (totalCount > rows.length) {
        return this.buildTileError(
          tile,
          `This metric needs the full preview set, but only ${rows.length} of ${totalCount} rows were available. Narrow the report or switch to a count tile.`,
          report,
          preview.warnings || [],
        );
      }

      const numericValues = rows
        .map((row) => this.toNumericValue(this.getPreviewCellValue(preview, row, tile.fieldKey || '')))
        .filter((value): value is number => Number.isFinite(value));

      if (!numericValues.length) {
        return this.buildTileError(
          tile,
          `No numeric values were found for "${tile.fieldKey}".`,
          report,
          preview.warnings || [],
        );
      }

      const rawValue = this.aggregateValues(numericValues, tile.metricType);
      return {
        tileId: tile.id,
        title: tile.title,
        reportId,
        reportName: this.getRenderedReportName(report, tile),
        reportDescription: this.getReportDescription(report),
        ...this.buildTilePreviewDisplay(preview),
        status: 'success',
        displayValue: this.formatValue(rawValue, tile.valueFormat, tile.precision),
        helperText: `${this.getMetricTypeLabel(tile.metricType)} of ${tile.fieldKey} from ${numericValues.length} rows`,
        warnings: preview.warnings || [],
        rawValue,
      };
    } catch (error: any) {
      return this.buildTileError(tile, error?.message || 'Unable to render tile.', report);
    }
  }

  private buildTileError(
    tile: PayrollDashboardTileDefinition,
    errorMessage: string,
    report?: ScheduledReport | null,
    warnings: string[] = [],
  ): DashboardTileRenderResult {
    return {
      tileId: tile.id,
      title: tile.title,
      reportId: tile.reportId,
      reportName: report?.name || this.getReportName(tile.reportId),
      reportDescription: this.getReportDescription(report),
      previewColumns: [],
      previewRows: [],
      previewTotalCount: 0,
      previewTimezone: undefined,
      status: 'error',
      displayValue: '--',
      helperText: '',
      warnings,
      errorMessage,
      rawValue: null,
    };
  }

  private getPreview(
    reportId: string,
    previewCache: Map<string, Promise<ScheduledReportPreview>>,
    dashboardId: string,
  ): Promise<ScheduledReportPreview> {
    const cacheKey = this.canManageDashboards ? `admin:${reportId}` : `dashboard:${dashboardId}:${reportId}`;
    if (!previewCache.has(cacheKey)) {
      previewCache.set(cacheKey, this.loadPreview(reportId, dashboardId));
    }
    return previewCache.get(cacheKey) as Promise<ScheduledReportPreview>;
  }

  private async loadPreview(reportId: string, dashboardId: string): Promise<ScheduledReportPreview> {
    const report = this.reports.find((item) => item.id === reportId) || null;
    const runtimeParameters = await this.promptRuntimeParameters(report);
    if (runtimeParameters === null) {
      throw new Error('Runtime parameter entry was canceled.');
    }
    const overrides: ScheduledReportPreviewOverrides | undefined = runtimeParameters
      ? { runtimeParameters }
      : undefined;
    return this.canManageDashboards
      ? this.scheduledReportsService.previewSaved(reportId, overrides, this.detailPreviewLimit)
      : this.dashboardsService.previewDashboardReport(dashboardId, reportId, this.detailPreviewLimit, overrides);
  }

  private getReportDescription(report?: ScheduledReport | null): string {
    return `${report?.description || ''}`.trim();
  }

  private getRenderedReportName(report: ScheduledReport | null, tile: PayrollDashboardTileDefinition): string {
    return `${report?.name || tile.title || ''}`.trim();
  }

  private buildDashboardEmailDeliveryOverride(report: ScheduledReport | null, email: string): ScheduledReportRunDeliveryOverride {
    const deliveryConfig = (report?.deliveryConfig || {}) as Record<string, any>;
    return {
      deliveryChannel: 'email',
      deliveryConfig: {
        to: [email],
        cc: [],
        bcc: [],
        subjectTemplate: `${deliveryConfig.subjectTemplate || deliveryConfig.subject || `Scheduled report: ${report?.name || 'Report'}`}`,
        bodyTemplate: `${deliveryConfig.bodyTemplate || deliveryConfig.bodyText || 'Attached is the scheduled report.'}`,
        attachFile: true,
      },
    };
  }

  private async promptRuntimeParameters(report: ScheduledReport | null): Promise<Record<string, any> | null | undefined> {
    const runtimeFilters = this.getRuntimeFilters(report);
    if (!runtimeFilters.length) {
      return undefined;
    }

    const dialogRef = this.dialog.open<
      PayrollDashboardReportParametersDialogComponent,
      { reportName: string; filters: ScheduledReportDefinitionFilter[]; fields: ScheduledReportFieldDefinition[]; valueOptions: Record<string, ScheduledReportValueOption[]> },
      PayrollDashboardReportParameterDialogResult
    >(PayrollDashboardReportParametersDialogComponent, {
      width: '620px',
      data: {
        reportName: report?.name || 'Report',
        filters: runtimeFilters,
        fields: this.fieldCatalog,
        valueOptions: this.reportValueOptions,
      },
      autoFocus: false,
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result || null;
  }

  private getRuntimeFilters(report: ScheduledReport | null): ScheduledReportDefinitionFilter[] {
    return (report?.definitionJson?.filters || [])
      .filter((filter) => filter?.valueMode === 'runtime' && filter.parameterKey);
  }

  private buildTilePreviewDisplay(
    preview: ScheduledReportPreview,
  ): Pick<DashboardTileRenderResult, 'previewColumns' | 'previewRows' | 'previewTotalCount' | 'previewTimezone'> {
    return {
      previewColumns: (preview.columns || []).map((column, index) => ({
        header: `${column.header || column.field || `Column ${index + 1}`}`.trim(),
        field: column.field,
      })),
      previewRows: (Array.isArray(preview.sampleRows) ? preview.sampleRows : []).slice(0, this.tilePreviewRowLimit),
      previewTotalCount: Number(preview.totalCount || 0),
      previewTimezone: preview.runContext?.timezone,
    };
  }

  private formatTilePreviewDateValue(tile: DashboardTileRenderResult, value: any): string | null {
    const parsedDate = this.parseTilePreviewDateValue(value);
    if (!parsedDate) {
      return null;
    }

    return new Intl.DateTimeFormat('en-US', {
      timeZone: tile.previewTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).format(parsedDate);
  }

  private parseTilePreviewDateValue(value: any): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed || !this.looksLikeDateTimeValue(trimmed)) {
      return null;
    }

    const parsedDate = new Date(trimmed);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private looksLikeDateTimeValue(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?$/.test(value);
  }

  private getTilePreviewSortKey(tile: DashboardTileRenderResult): string {
    return tile.tileId || tile.reportId;
  }

  private getTilePreviewSortValue(tile: DashboardTileRenderResult, value: any): string | number {
    if (value === null || value === undefined) {
      return '';
    }

    const formattedValue = this.getTilePreviewCellText(tile, value).trim();
    const numericValue = Number(formattedValue);
    if (formattedValue !== '' && !Number.isNaN(numericValue) && /^-?\d+(\.\d+)?$/.test(formattedValue)) {
      return numericValue;
    }

    const dateValue = Date.parse(formattedValue);
    if (!Number.isNaN(dateValue) && /\d{4}-\d{2}-\d{2}|[A-Z][a-z]{2}/.test(formattedValue)) {
      return dateValue;
    }

    return formattedValue.toLowerCase();
  }

  private async ensureTileFieldOptions(reportId: string): Promise<void> {
    if (!reportId || this.tileColumnOptionsByReportId[reportId] || this.tileColumnLoadingIds.has(reportId)) {
      return;
    }

    this.tileColumnLoadingIds.add(reportId);
    try {
      const report = this.reports.find((item) => item.id === reportId) || null;
      const runtimeParameters = await this.promptRuntimeParameters(report);
      if (runtimeParameters === null) {
        return;
      }
      const overrides: ScheduledReportPreviewOverrides | undefined = runtimeParameters
        ? { runtimeParameters }
        : undefined;
      const preview = await this.scheduledReportsService.previewSaved(reportId, overrides, 50);
      const seen = new Set<string>();
      const options = (preview.columns || [])
        .map((column, index) => {
          const value = `${column.field || column.header || ''}`.trim();
          const header = `${column.header || column.field || `Column ${index + 1}`}`.trim();
          if (!value || seen.has(value)) {
            return null;
          }
          seen.add(value);
          return {
            value,
            label: column.field && column.header && column.field !== column.header
              ? `${column.header} (${column.field})`
              : header,
          } as DashboardColumnOption;
        })
        .filter((option): option is DashboardColumnOption => Boolean(option));

      this.tileColumnOptionsByReportId[reportId] = options;
    } catch {
      this.tileColumnOptionsByReportId[reportId] = [];
    } finally {
      this.tileColumnLoadingIds.delete(reportId);
    }
  }

  private getPreviewCellValue(preview: ScheduledReportPreview, row: Record<string, any>, fieldKey: string): any {
    if (row[fieldKey] !== undefined) {
      return row[fieldKey];
    }

    const column = (preview.columns || []).find((item) => item.field === fieldKey || item.header === fieldKey);
    if (column?.header && row[column.header] !== undefined) {
      return row[column.header];
    }
    if (column?.field && row[column.field] !== undefined) {
      return row[column.field];
    }

    return undefined;
  }

  private toNumericValue(value: any): number {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(/[$,%\s,]/g, '');
      return Number(normalized);
    }
    return Number.NaN;
  }

  private aggregateValues(values: number[], metricType: PayrollDashboardMetricType): number {
    switch (metricType) {
      case 'sum':
        return values.reduce((total, value) => total + value, 0);
      case 'average':
        return values.reduce((total, value) => total + value, 0) / values.length;
      case 'minimum':
        return Math.min(...values);
      case 'maximum':
        return Math.max(...values);
      default:
        return values.length;
    }
  }

  private formatValue(value: number, format: PayrollDashboardValueFormat, precision: number): string {
    if (!Number.isFinite(value)) {
      return '--';
    }

    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value);
    }

    if (format === 'percent') {
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(value);
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(value);
  }
}
